import { useState, useEffect } from 'react'
import { usePlayerStore, useAuthStore } from '../stores'
import PlayerDetail from './PlayerDetail'
import EditPlayer from './EditPlayer'
import PlayerCard from './PlayerCard'
import { notifyVerificationChange } from '../utils/notify'
import { Trash2, Edit, Eye, EyeOff, ShieldCheck, Award } from 'lucide-react'

interface PlayerDatabaseProps {
  initialSelectedPlayer?: string | null
  onPlayerSelected?: () => void
}

function PlayerDatabase({ initialSelectedPlayer, onPlayerSelected }: PlayerDatabaseProps) {
  const { players, deletePlayer, updatePlayer } = usePlayerStore()
  const { user } = useAuthStore()
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(initialSelectedPlayer || null)
  const [editingPlayer, setEditingPlayer] = useState<string | null>(null)
  const [filteredPlayers, setFilteredPlayers] = useState(players)
  const [searchType, setSearchType] = useState('name')
  const [filter, setFilter] = useState('')

  useEffect(() => {
    if (initialSelectedPlayer) {
      setSelectedPlayer(initialSelectedPlayer)
    }
  }, [initialSelectedPlayer])

  const visiblePlayers = players.filter((p) => {
    if (user?.role === 'scout') {
      return p.scout === user.email
    }
    return true
  })

  useEffect(() => {
    const filtered = visiblePlayers.filter((player) => {
      if (!filter) return true
      
      const searchTerm = filter.toLowerCase()
      switch (searchType) {
        case 'name':
          return player.name.toLowerCase().includes(searchTerm)
        case 'club':
          return player.club?.toLowerCase().includes(searchTerm)
        case 'scout':
          return player.scout?.toLowerCase().includes(searchTerm)
        default:
          return player.name.toLowerCase().includes(searchTerm)
      }
    })
    setFilteredPlayers(filtered)
  }, [visiblePlayers, filter, searchType])

  if (selectedPlayer) {
    const player = players.find(p => p.id === selectedPlayer)
    if (player) {
      return (
        <PlayerDetail
          player={player}
          onEdit={() => {
            setEditingPlayer(selectedPlayer)
            setSelectedPlayer(null)
          }}
          onBack={() => {
            setSelectedPlayer(null)
            if (onPlayerSelected) onPlayerSelected()
          }}
        />
      )
    }
  }

  if (editingPlayer) {
    const player = players.find(p => p.id === editingPlayer)
    if (player) {
      return (
        <EditPlayer
          player={player}
          onSave={() => {
            setEditingPlayer(null)
            setSelectedPlayer(editingPlayer)
          }}
          onCancel={() => setEditingPlayer(null)}
        />
      )
    }
  }

  const handleApproval = async (playerId: string, approved: boolean) => {
    try {
      await updatePlayer(playerId, { is_verified: approved }, { useServiceRole: true })
      // Notify the player about verification change
      notifyVerificationChange(
        playerId,
        approved ? 'verified' : 'unverified',
        user?.name
      )
    } catch (error) {
      console.error('Failed to update approval status:', error)
    }
  }

  const handleTopTalentToggle = async (playerId: string, isTopTalent: boolean) => {
    try {
      await updatePlayer(playerId, { is_top_talent: isTopTalent }, { useServiceRole: true })
    } catch (error) {
      console.error('Failed to update top talent status:', error)
    }
  }

  const handleVisibilityToggle = async (playerId: string, isPublic: boolean) => {
    try {
      await updatePlayer(playerId, { is_public: isPublic }, { useServiceRole: true })
    } catch (error) {
      console.error('Failed to update player visibility:', error)
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-[#161b22] border border-gray-800 shadow-sm rounded-xl p-6">
        <h2 className="text-2xl font-bold text-white mb-6">Player Database</h2>

        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex gap-2 flex-1">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as any)}
              className="px-4 py-2 bg-[#0d1117] border border-gray-800 rounded-lg text-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition"
            >
              <option value="name">Name</option>
              <option value="club">Club</option>
              <option value="scout">Scout</option>
            </select>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={`Search by ${searchType}...`}
              className="flex-1 px-4 py-2 bg-[#0d1117] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition"
            />
          </div>
          <div className="text-gray-400 text-sm font-medium bg-[#0d1117] px-3 py-1 rounded-full border border-gray-800">
            {filteredPlayers.length} {filteredPlayers.length === 1 ? 'player' : 'players'}
          </div>
        </div>
      </div>

      {filteredPlayers.length === 0 ? (
        <div className="bg-[#161b22] border border-gray-800 shadow-sm rounded-xl p-12 text-center">
          <div className="mb-4 text-gray-600 flex justify-center">
            <Eye size={48} />
          </div>
          <p className="text-white font-semibold mb-2">No players found</p>
          <p className="text-gray-500 text-sm max-w-xs mx-auto">
            {players.length === 0
              ? 'Start logging players to build your localized scouting database.'
              : 'We couldn\'t find any players matching your current search term.'}
          </p>
        </div>
       ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-5 lg:gap-6 px-1 sm:px-0">
           {filteredPlayers.map(player => (
             <PlayerCard
               key={player.id}
               player={player}
               onViewProfile={(id) => setSelectedPlayer(id)}
               adminActions={
                <div className="flex gap-2">
                  {user?.role === 'admin' && (
                    <>
                      <button
                        onClick={() => handleApproval(player.id, !player.is_verified)}
                        className={`p-2 rounded-xl border transition-colors ${
                          player.is_verified 
                            ? 'bg-emerald-900/20 border-emerald-700/50 text-emerald-400' 
                            : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                        }`}
                        title={player.is_verified ? 'Approved' : 'Click to Approve'}
                      >
                        <ShieldCheck size={18} />
                      </button>
                      <button
                        onClick={() => handleTopTalentToggle(player.id, !player.is_top_talent)}
                        className={`p-2 rounded-xl border transition-colors ${
                          player.is_top_talent 
                            ? 'bg-[#7cff01]/10 border-[#7cff01]/50 text-[#7cff01]' 
                            : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                        }`}
                        title={player.is_top_talent ? 'Top Talent' : 'Mark as Top Talent'}
                      >
                        <Award size={18} />
                      </button>
                      <button
                        onClick={() => handleVisibilityToggle(player.id, player.is_public === false)}
                        className={`p-2 rounded-xl border transition-colors ${
                          player.is_public !== false
                            ? 'bg-sky-900/20 border-sky-700/50 text-sky-400'
                            : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                        }`}
                        title={player.is_public !== false ? 'Visible on Explore' : 'Hidden from Explore'}
                      >
                        {player.is_public !== false ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                    </>
                  )}
                  {((user?.role !== 'admin' && player.scout === user?.email) || user?.role === 'admin') && (
                    <>
                      <button
                        onClick={() => setEditingPlayer(player.id)}
                        className="p-2 bg-gray-800 border border-gray-700 rounded-xl text-gray-400 hover:text-yellow-500 transition-colors"
                        title="Edit Player"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete ${player.name}? This action cannot be undone.`)) {
                            void deletePlayer(player.id, { useServiceRole: true })
                          }
                        }}
                        className="p-2 bg-gray-800 border border-gray-700 rounded-xl text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete Player"
                      >
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </div>
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default PlayerDatabase
